import { useState, useEffect, useId } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router";
import {
  ArrowLeft,
  FileText,
  Briefcase,
  Phone,
  HelpCircle,
  File,
  Check,
} from "lucide-react";
import { useToast } from "../../components/ui/Toast";
import { CREATE_PAGE, evictPageListings } from "./pages/mutations";
import type { PageForEdit } from "./pages/types";
import { slugify } from "../../utils/slugify";
import { BLUEPRINTS } from "./blueprints";

interface CreateResponse {
  createPage: PageForEdit | null;
}

const BLUEPRINT_ICONS: Record<string, typeof FileText> = {
  about: FileText,
  services: Briefcase,
  contact: Phone,
  faq: HelpCircle,
  blank: File,
};

export function PageCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const titleId = useId();
  const slugId = useId();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = useState<string>("blank");

  // Auto-generate slug from title until the user manually edits the slug field
  useEffect(() => {
    if (!slugTouched) {
      setSlug(slugify(title));
    }
  }, [title, slugTouched]);

  const [create, { loading: saving }] = useMutation<CreateResponse>(
    CREATE_PAGE,
    {
      refetchQueries: ["AllPagesForListing"],
      update: evictPageListings,
    },
  );

  const blueprint = BLUEPRINTS.find((b) => b.id === selectedBlueprint);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedSlug = slug.trim();
    if (!trimmedTitle || !trimmedSlug) return;

    // Build draft_content from the selected blueprint
    const draftContent =
      blueprint && blueprint.sections.length > 0
        ? JSON.stringify({ content: blueprint.sections })
        : null;

    try {
      const result = await create({
        variables: {
          slug: trimmedSlug,
          title: trimmedTitle,
          draftContent,
        },
      });
      const created = result.data?.createPage;
      if (!created) throw new Error("Couldn't create page");
      toast.push({
        kind: "success",
        title: "Page created",
        description: `"${created.title || created.slug}" is ready to edit.`,
      });
      navigate(`/website/pages/${created.id}`, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      // Surface slug uniqueness errors in a user-friendly way
      const isSlugConflict =
        typeof message === "string" &&
        message.toLowerCase().includes("already exists");
      toast.push({
        kind: "error",
        title: "Couldn't create page",
        description: isSlugConflict
          ? "This URL path is already in use. Try a different one."
          : message,
      });
    }
  }

  return (
    <div className="min-h-full">
      <header className="hair-b border-b sticky top-[var(--banner-h)] z-10 flex h-14 items-center gap-3 bg-paper/90 px-7 backdrop-blur">
        <button
          type="button"
          onClick={() => navigate("/website/pages")}
          className="flex items-center gap-1.5 text-[13px] text-ink-3 hover:text-ink transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.8} />
          Pages
        </button>
        <span className="text-ink-5">&rsaquo;</span>
        <span className="text-[13px] font-medium text-ink">New page</span>
      </header>

      <div className="mx-auto max-w-2xl px-7 py-10">
        <h1 className="font-display mb-8 text-[24px] font-medium tracking-tight">
          New page
        </h1>

        <form onSubmit={handleSubmit} className="space-y-7">
          {/* Title field -- primary, required */}
          <div>
            <label
              htmlFor={titleId}
              className="mb-1.5 block text-[13px] font-medium text-ink"
            >
              Page title <span className="text-rust">*</span>
            </label>
            <input
              id={titleId}
              type="text"
              required
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="About Us"
              className="hair w-full rounded-md border bg-paper px-3 py-2.5 text-[15px] placeholder:text-ink-5 focus:border-tide focus:outline-none"
            />
          </div>

          {/* URL path -- auto-generated, editable */}
          <div>
            <label
              htmlFor={slugId}
              className="mb-1.5 block text-[13px] font-medium text-ink"
            >
              URL path
            </label>
            <div className="flex items-center gap-0">
              <span className="flex h-[42px] items-center rounded-l-md border border-r-0 bg-canvas px-2.5 text-[13px] text-ink-4 font-mono hair">
                /
              </span>
              <input
                id={slugId}
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, ""),
                  );
                }}
                placeholder="about-us"
                className="hair w-full rounded-r-md border bg-paper px-3 py-2.5 font-mono text-[13px] placeholder:text-ink-5 focus:border-tide focus:outline-none"
              />
            </div>
            <p className="mt-1 text-[11.5px] text-ink-4">
              Auto-generated from title. Edit to customize.
            </p>
          </div>

          {/* Blueprint picker */}
          <div>
            <p className="mb-3 text-[13px] font-medium text-ink">
              Start from a template
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {BLUEPRINTS.map((bp) => {
                const Icon = BLUEPRINT_ICONS[bp.id] ?? File;
                const active = selectedBlueprint === bp.id;
                return (
                  <button
                    key={bp.id}
                    type="button"
                    onClick={() => setSelectedBlueprint(bp.id)}
                    className={`group relative flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all ${
                      active
                        ? "border-tide bg-tide/[0.04] ring-1 ring-tide/30"
                        : "border-ink-6 bg-paper hover:border-ink-5 hover:bg-paper-2"
                    }`}
                  >
                    {active && (
                      <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-tide text-white">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                    <Icon
                      size={20}
                      strokeWidth={1.5}
                      className={active ? "text-tide" : "text-ink-4"}
                    />
                    <div>
                      <div
                        className={`text-[13px] font-semibold ${
                          active ? "text-tide" : "text-ink"
                        }`}
                      >
                        {bp.name}
                      </div>
                      <div className="mt-0.5 text-[11.5px] leading-snug text-ink-4">
                        {bp.description}
                      </div>
                    </div>
                    <span className="mt-auto text-[10.5px] font-medium text-ink-5">
                      {bp.sectionCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div className="pt-1">
            <button
              type="submit"
              disabled={saving || !title.trim() || !slug.trim()}
              className="rounded-md bg-tide px-4 py-2.5 text-[13px] font-medium text-paper-hi hover:bg-tide-d glow-tide disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Creating..." : "Create page"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
